import { z } from 'zod';
import { CountryCodeSchema } from './geo';

/* ------------------------------------------------------------------ */
/* Trust & safety (W17): ratings + reviews, fraud + abuse, restaurant  */
/* integrity + hygiene.                                                */
/*                                                                     */
/* Customers rate food and delivery after a delivered order (photos,   */
/* dish likes), restaurants reply once and dispute with a reason code, */
/* Ops moderate in Capsico OS › Trust & safety. Hard rules act on their */
/* own (checkout freeze, COD downgrade, licence-expiry pause, spike     */
/* holds); scores and proposals always wait for an Ops decision.        */
/* ------------------------------------------------------------------ */

const Id = z.uuid();
const Stars = z.number().int().min(1).max(5);

/** Polling for the Ops queues (no live socket for trust). */
export const TRUST_POLL_SECONDS = 30;
/** Longest review text and reply. */
export const REVIEW_TEXT_MAX = 500;
export const REVIEW_REPLY_MAX = 500;
/** Photos per review. */
export const REVIEW_MAX_PHOTOS = 3;
/** Largest review / re-audit photo (bytes). */
export const TRUST_PHOTO_MAX_BYTES = 8 * 1024 * 1024;
export const TRUST_PHOTO_TYPES = ['image/jpeg', 'image/png', 'image/webp'] as const;
/** Photos on one re-audit close. */
export const REAUDIT_MAX_PHOTOS = 6;

/* ---------------- reviews ---------------- */

/**
 * published: shown and counted. held: a 1-star spike hold — not shown, not counted until Ops
 * release it. hidden: text and photos hidden (abusive), stars still count. removed: kept for audit
 * but neither shown nor counted.
 */
export const REVIEW_STATUSES = ['published', 'held', 'hidden', 'removed'] as const;
export const ReviewStatusSchema = z.enum(REVIEW_STATUSES);
export type ReviewStatus = z.infer<typeof ReviewStatusSchema>;

export const REVIEW_STATUS_LABELS: Record<ReviewStatus, string> = {
  published: 'Published',
  held: 'On hold',
  hidden: 'Text hidden',
  removed: 'Removed from score',
};

export const REVIEW_DISPUTE_REASONS = [
  'abusive',
  'wrong_restaurant',
  'refunded_order',
  'competitor',
  'other',
] as const;
export const ReviewDisputeReasonSchema = z.enum(REVIEW_DISPUTE_REASONS);
export type ReviewDisputeReason = z.infer<typeof ReviewDisputeReasonSchema>;

export const REVIEW_DISPUTE_REASON_LABELS: Record<ReviewDisputeReason, string> = {
  abusive: 'Abusive or offensive',
  wrong_restaurant: 'About another restaurant',
  refunded_order: 'Order was refunded',
  competitor: 'Looks like a competitor',
  other: 'Something else',
};

/** Ops decision on a dispute (and on a held review: publish = keep). */
export const REVIEW_DECISIONS = ['keep', 'hide', 'remove'] as const;
export const ReviewDecisionSchema = z.enum(REVIEW_DECISIONS);
export type ReviewDecision = z.infer<typeof ReviewDecisionSchema>;

export const REVIEW_DECISION_LABELS: Record<ReviewDecision, string> = {
  keep: 'Keep',
  hide: 'Hide text',
  remove: 'Remove from score',
};

export const DISPUTE_STATUSES = ['open', 'decided'] as const;
export const DisputeStatusSchema = z.enum(DISPUTE_STATUSES);
export type DisputeStatus = z.infer<typeof DisputeStatusSchema>;

/** Why a customer cannot rate an order (pending list / submit refusal). */
export const REVIEW_BLOCKS = [
  'not_delivered',
  'window_closed',
  'already_reviewed',
  'self_review',
  'self_order',
  'not_yours',
] as const;
export type ReviewBlock = (typeof REVIEW_BLOCKS)[number];

export const ReviewPhotoSchema = z.object({ id: Id, url: z.string() });
export type ReviewPhoto = z.infer<typeof ReviewPhotoSchema>;

export const ReviewReplySchema = z.object({
  text: z.string(),
  at: z.string(),
  /** Partner side only: the reply can still be edited until then. */
  editableUntil: z.string().nullable(),
  version: z.number().int(),
});
export type ReviewReply = z.infer<typeof ReviewReplySchema>;

/* customer: pending to rate + my reviews */

export const PendingRatingSchema = z.object({
  orderId: Id,
  orderNo: z.number().int(),
  outletId: Id,
  outletName: z.string(),
  deliveredAt: z.string(),
  /** Rate by this time (delivered + trust.review_window_hours). */
  rateBy: z.string(),
  /** False when the order had no rider (delivery stars are skipped). */
  hasRider: z.boolean(),
  dishes: z.array(z.object({ menuItemId: Id, name: z.string() })),
});
export type PendingRating = z.infer<typeof PendingRatingSchema>;

export const MyReviewSchema = z.object({
  id: Id,
  orderId: Id,
  orderNo: z.number().int(),
  outletId: Id,
  outletName: z.string(),
  foodStars: Stars,
  deliveryStars: Stars.nullable(),
  text: z.string().nullable(),
  photos: z.array(ReviewPhotoSchema),
  likedDishIds: z.array(Id),
  unlikedDishIds: z.array(Id),
  /** The customer always sees their own review; held / hidden reviews say so. */
  status: ReviewStatusSchema,
  editableUntil: z.string(),
  reply: ReviewReplySchema.nullable(),
  createdAt: z.string(),
  version: z.number().int(),
});
export type MyReview = z.infer<typeof MyReviewSchema>;

export const MyReviewsSchema = z.object({
  pending: z.array(PendingRatingSchema),
  reviews: z.array(MyReviewSchema),
});
export type MyReviews = z.infer<typeof MyReviewsSchema>;

export const ReviewSubmitInputSchema = z.object({
  orderId: Id,
  foodStars: Stars,
  /** Omit (or null) when the order had no rider. */
  deliveryStars: Stars.nullable().optional(),
  text: z.string().trim().max(REVIEW_TEXT_MAX).optional(),
  photoIds: z.array(Id).max(REVIEW_MAX_PHOTOS).default([]),
  likedDishIds: z.array(Id).max(50).default([]),
  unlikedDishIds: z.array(Id).max(50).default([]),
});
export type ReviewSubmitInput = z.infer<typeof ReviewSubmitInputSchema>;

export const ReviewEditInputSchema = ReviewSubmitInputSchema.omit({ orderId: true }).extend({
  expectedVersion: z.number().int().min(1),
});
export type ReviewEditInput = z.infer<typeof ReviewEditInputSchema>;

/** Like / unlike one dish of a delivered order (also allowed without a review). */
export const DishLikeInputSchema = z.object({
  orderId: Id,
  menuItemId: Id,
  /** true = like, false = unlike, null = clear. */
  liked: z.boolean().nullable(),
});
export type DishLikeInput = z.infer<typeof DishLikeInputSchema>;

export const DishLikeResultSchema = z.object({
  orderId: Id,
  menuItemId: Id,
  liked: z.boolean().nullable(),
});
export type DishLikeResult = z.infer<typeof DishLikeResultSchema>;

/* photo upload (review photos + re-audit photos) */

export const TrustPhotoStartInputSchema = z.object({
  contentType: z.enum(TRUST_PHOTO_TYPES),
  sizeBytes: z.number().int().min(1).max(TRUST_PHOTO_MAX_BYTES),
});
export type TrustPhotoStartInput = z.infer<typeof TrustPhotoStartInputSchema>;

export const TrustPhotoTicketSchema = z.object({
  photoId: Id,
  url: z.string(),
  method: z.literal('PUT'),
  headers: z.record(z.string(), z.string()),
  expiresAt: z.string(),
});
export type TrustPhotoTicket = z.infer<typeof TrustPhotoTicketSchema>;

export const TrustPhotoDoneSchema = z.object({ photoId: Id, uploaded: z.boolean() });
export type TrustPhotoDone = z.infer<typeof TrustPhotoDoneSchema>;

/* public restaurant reviews */

export const HYGIENE_BADGES = ['green', 'amber', 'hidden'] as const;
export const HygieneBadgeSchema = z.enum(HYGIENE_BADGES);
export type HygieneBadge = z.infer<typeof HygieneBadgeSchema>;

export const HYGIENE_BADGE_LABELS: Record<HygieneBadge, string> = {
  green: 'Hygiene: very good',
  amber: 'Hygiene: good',
  hidden: 'Not shown',
};

export const RatingSummarySchema = z.object({
  /** Weighted 90-day mean blended with the prior; null while the restaurant is "New". */
  average: z.number().nullable(),
  /** Counted ratings in the last 90 days. */
  count: z.number().int(),
  isNew: z.boolean(),
  /** Counted ratings by star, index 0 = 5 stars … index 4 = 1 star. */
  distribution: z.array(z.number().int()).length(5),
  /** Public badge (null when hidden). */
  hygiene: HygieneBadgeSchema.exclude(['hidden']).nullable(),
});
export type RatingSummary = z.infer<typeof RatingSummarySchema>;

export const PublicReviewSchema = z.object({
  id: Id,
  /** First name + initial ("Asha V."). */
  author: z.string(),
  foodStars: Stars,
  text: z.string().nullable(),
  photos: z.array(ReviewPhotoSchema),
  likedDishes: z.array(z.string()),
  createdAt: z.string(),
  reply: z.object({ text: z.string(), at: z.string() }).nullable(),
});
export type PublicReview = z.infer<typeof PublicReviewSchema>;

export const PUBLIC_REVIEW_SORTS = ['recent', 'highest', 'lowest', 'photos'] as const;

export const PublicReviewQuerySchema = z.object({
  sort: z.enum(PUBLIC_REVIEW_SORTS).default('recent'),
  page: z.coerce.number().int().min(1).max(500).default(1),
  limit: z.coerce.number().int().min(1).max(50).default(20),
});
export type PublicReviewQuery = z.infer<typeof PublicReviewQuerySchema>;

export const PublicReviewPageSchema = z.object({
  outletId: Id,
  summary: RatingSummarySchema,
  reviews: z.array(PublicReviewSchema),
  page: z.number().int(),
  hasMore: z.boolean(),
});
export type PublicReviewPage = z.infer<typeof PublicReviewPageSchema>;

/* partner: Feedback › Reviews */

export const PartnerReviewQuerySchema = z.object({
  outletId: Id.optional(),
  filter: z.enum(['all', 'unreplied', 'low', 'disputed']).default('all'),
  page: z.coerce.number().int().min(1).max(500).default(1),
  limit: z.coerce.number().int().min(1).max(100).default(30),
});
export type PartnerReviewQuery = z.infer<typeof PartnerReviewQuerySchema>;

export const PartnerReviewSchema = z.object({
  id: Id,
  orderId: Id,
  orderNo: z.number().int(),
  outletId: Id,
  outletName: z.string(),
  author: z.string(),
  foodStars: Stars,
  deliveryStars: Stars.nullable(),
  text: z.string().nullable(),
  photos: z.array(ReviewPhotoSchema),
  likedDishes: z.array(z.string()),
  unlikedDishes: z.array(z.string()),
  status: ReviewStatusSchema,
  createdAt: z.string(),
  reply: ReviewReplySchema.nullable(),
  canReply: z.boolean(),
  dispute: z
    .object({
      id: Id,
      reason: ReviewDisputeReasonSchema,
      status: DisputeStatusSchema,
      decision: ReviewDecisionSchema.nullable(),
      dueAt: z.string(),
    })
    .nullable(),
  canDispute: z.boolean(),
});
export type PartnerReview = z.infer<typeof PartnerReviewSchema>;

export const PartnerReviewListSchema = z.object({
  summary: RatingSummarySchema,
  reviews: z.array(PartnerReviewSchema),
  page: z.number().int(),
  hasMore: z.boolean(),
});
export type PartnerReviewList = z.infer<typeof PartnerReviewListSchema>;

export const ReviewReplyInputSchema = z.object({
  text: z.string().trim().min(2).max(REVIEW_REPLY_MAX),
});
export type ReviewReplyInput = z.infer<typeof ReviewReplyInputSchema>;

export const ReviewReplyEditInputSchema = ReviewReplyInputSchema.extend({
  expectedVersion: z.number().int().min(1),
});
export type ReviewReplyEditInput = z.infer<typeof ReviewReplyEditInputSchema>;

export const ReviewDisputeInputSchema = z.object({
  reason: ReviewDisputeReasonSchema,
  note: z.string().trim().max(300).optional(),
});
export type ReviewDisputeInput = z.infer<typeof ReviewDisputeInputSchema>;

/* ---------------- fraud + abuse ---------------- */

export const TRUST_SUBJECT_TYPES = ['customer', 'rider', 'restaurant', 'device', 'order'] as const;
export const TrustSubjectTypeSchema = z.enum(TRUST_SUBJECT_TYPES);
export type TrustSubjectType = z.infer<typeof TrustSubjectTypeSchema>;

export const TRUST_FLAG_KINDS = [
  'device_accounts',
  'device_new_numbers',
  'cod_downgrade',
  'refund_risk',
  'photo_reuse',
  'self_order',
  'self_review',
  'review_spike',
  'rider_risk',
  'reaudit',
  'safety_pause',
  'licence_expiry',
  'integrity_failed',
  'scrape_abuse',
] as const;
export const TrustFlagKindSchema = z.enum(TRUST_FLAG_KINDS);
export type TrustFlagKind = z.infer<typeof TrustFlagKindSchema>;

export const TRUST_FLAG_KIND_LABELS: Record<TrustFlagKind, string> = {
  device_accounts: 'Too many accounts on one device',
  device_new_numbers: 'Many new numbers on one device',
  cod_downgrade: 'Cash on delivery switched off',
  refund_risk: 'High refund risk',
  photo_reuse: 'Complaint photo reused',
  self_order: 'Restaurant ordering from itself',
  self_review: 'Self-review attempt',
  review_spike: '1-star spike hold',
  rider_risk: 'Rider risk — suspension proposal',
  reaudit: 'Re-audit opened',
  safety_pause: 'Safety complaint — pause proposal',
  licence_expiry: 'Food licence expiring',
  integrity_failed: 'App integrity check failed',
  scrape_abuse: 'Automated browsing (scraping)',
};

/**
 * What confirming a flag does. none: the decision is recorded only (the hard rule already acted).
 * suspend_rider / pause_outlet: the proposal is carried out on confirm.
 */
export const TRUST_FLAG_EFFECTS: Record<TrustFlagKind, 'none' | 'suspend_rider' | 'pause_outlet'> =
  {
    device_accounts: 'none',
    device_new_numbers: 'none',
    cod_downgrade: 'none',
    refund_risk: 'none',
    photo_reuse: 'none',
    self_order: 'none',
    self_review: 'none',
    review_spike: 'none',
    rider_risk: 'suspend_rider',
    reaudit: 'none',
    safety_pause: 'pause_outlet',
    licence_expiry: 'none',
    integrity_failed: 'none',
    scrape_abuse: 'none',
  };

export const TRUST_FLAG_STATUSES = ['open', 'confirmed', 'dismissed'] as const;
export const TrustFlagStatusSchema = z.enum(TRUST_FLAG_STATUSES);
export type TrustFlagStatus = z.infer<typeof TrustFlagStatusSchema>;

export const TrustFlagSchema = z.object({
  id: Id,
  kind: TrustFlagKindSchema,
  label: z.string(),
  subjectType: TrustSubjectTypeSchema,
  subjectId: z.string(),
  /** Customer / rider / outlet name when known. */
  subjectName: z.string().nullable(),
  orderId: Id.nullable(),
  outletId: Id.nullable(),
  score: z.number().int().nullable(),
  /** Plain facts the rule saw ("3 accounts on this phone", "Distance to outlet 9 m"). */
  facts: z.array(z.string()),
  status: TrustFlagStatusSchema,
  effect: z.enum(['none', 'suspend_rider', 'pause_outlet']),
  createdAt: z.string(),
  decidedBy: z.string().nullable(),
  decidedAt: z.string().nullable(),
  note: z.string().nullable(),
  version: z.number().int(),
});
export type TrustFlag = z.infer<typeof TrustFlagSchema>;

export const TrustFlagQuerySchema = z.object({
  status: TrustFlagStatusSchema.optional(),
  kind: TrustFlagKindSchema.optional(),
  subjectType: TrustSubjectTypeSchema.optional(),
  limit: z.coerce.number().int().min(1).max(500).default(200),
});
export type TrustFlagQuery = z.infer<typeof TrustFlagQuerySchema>;

export const TrustFlagListSchema = z.object({ rows: z.array(TrustFlagSchema) });
export type TrustFlagList = z.infer<typeof TrustFlagListSchema>;

/** Confirm / dismiss a flag, or decide a review / dispute: always a typed reason. */
export const TrustDecisionInputSchema = z.object({
  note: z.string().trim().min(3).max(300),
  expectedVersion: z.number().int().min(1),
});
export type TrustDecisionInput = z.infer<typeof TrustDecisionInputSchema>;

/* risk scores */

export const RISK_BANDS = ['green', 'amber', 'red'] as const;
export const RiskBandSchema = z.enum(RISK_BANDS);
export type RiskBand = z.infer<typeof RiskBandSchema>;

export const RISK_BAND_LABELS: Record<RiskBand, string> = {
  green: 'Low risk',
  amber: 'Watch',
  red: 'High risk',
};

export const RiskFactorSchema = z.object({
  key: z.string(),
  label: z.string(),
  /** The raw measurement ("12%", "3 accounts"). */
  value: z.string(),
  /** Points this factor added to the 0–100 score. */
  points: z.number(),
});
export type RiskFactor = z.infer<typeof RiskFactorSchema>;

export const DeviceLinkSchema = z.object({
  /** Only the first 10 characters of the hash (never the raw id). */
  device: z.string(),
  realm: z.enum(['customer', 'partner']),
  identityId: Id,
  name: z.string().nullable(),
  phoneMasked: z.string(),
  firstSeen: z.string(),
  lastSeen: z.string(),
  /** The partner's restaurant, for partner links. */
  restaurantName: z.string().nullable(),
});
export type DeviceLink = z.infer<typeof DeviceLinkSchema>;

export const RiskProfileSchema = z.object({
  subjectType: z.enum(['customer', 'rider', 'restaurant']),
  subjectId: z.string(),
  name: z.string().nullable(),
  country: CountryCodeSchema.nullable(),
  score: z.number().int().nullable(),
  band: RiskBandSchema.nullable(),
  factors: z.array(RiskFactorSchema),
  computedAt: z.string().nullable(),
  /** Customer only. */
  codRisky: z.boolean(),
  checkoutFrozen: z.string().nullable(),
  flags: z.array(TrustFlagSchema),
  /** Customer: every account that shared a device with them (2 hops). */
  devices: z.array(DeviceLinkSchema),
  stats: z.array(z.object({ label: z.string(), value: z.string() })),
});
export type RiskProfile = z.infer<typeof RiskProfileSchema>;

export const RiskSubjectParamsSchema = z.object({
  subjectType: z.enum(['customer', 'rider', 'restaurant']),
  subjectId: Id,
});
export type RiskSubjectParams = z.infer<typeof RiskSubjectParamsSchema>;

/* ---------------- Ops review moderation ---------------- */

export const OpsReviewSchema = z.object({
  id: Id,
  orderId: Id,
  orderNo: z.number().int(),
  outletId: Id,
  outletName: z.string(),
  customerId: Id,
  customerName: z.string().nullable(),
  foodStars: Stars,
  deliveryStars: Stars.nullable(),
  text: z.string().nullable(),
  photos: z.array(ReviewPhotoSchema),
  status: ReviewStatusSchema,
  weight: z.number(),
  holdId: Id.nullable(),
  languageFlag: z.boolean(),
  createdAt: z.string(),
  decidedBy: z.string().nullable(),
  note: z.string().nullable(),
  version: z.number().int(),
});
export type OpsReview = z.infer<typeof OpsReviewSchema>;

export const OpsReviewQuerySchema = z.object({
  status: ReviewStatusSchema.optional(),
  outletId: Id.optional(),
  limit: z.coerce.number().int().min(1).max(500).default(200),
});
export type OpsReviewQuery = z.infer<typeof OpsReviewQuerySchema>;

export const OpsReviewListSchema = z.object({ rows: z.array(OpsReviewSchema) });
export type OpsReviewList = z.infer<typeof OpsReviewListSchema>;

export const ReviewModerateInputSchema = TrustDecisionInputSchema.extend({
  decision: ReviewDecisionSchema,
});
export type ReviewModerateInput = z.infer<typeof ReviewModerateInputSchema>;

export const ReviewHoldSchema = z.object({
  id: Id,
  outletId: Id,
  outletName: z.string(),
  triggerCount: z.number().int(),
  heldReviews: z.number().int(),
  status: z.enum(['open', 'released']),
  startedAt: z.string(),
  releasedAt: z.string().nullable(),
  releasedBy: z.string().nullable(),
  version: z.number().int(),
});
export type ReviewHold = z.infer<typeof ReviewHoldSchema>;

export const ReviewHoldListSchema = z.object({ rows: z.array(ReviewHoldSchema) });
export type ReviewHoldList = z.infer<typeof ReviewHoldListSchema>;

/** Release a hold: every held review is kept (published) or removed from the score. */
export const HoldReleaseInputSchema = TrustDecisionInputSchema.extend({
  decision: z.enum(['keep', 'remove']),
});
export type HoldReleaseInput = z.infer<typeof HoldReleaseInputSchema>;

export const DisputeChecksSchema = z.object({
  orderExists: z.boolean(),
  delivered: z.boolean(),
  refunded: z.boolean(),
  languageFlag: z.boolean(),
  selfOrder: z.boolean(),
});
export type DisputeChecks = z.infer<typeof DisputeChecksSchema>;

export const ReviewDisputeSchema = z.object({
  id: Id,
  reviewId: Id,
  outletId: Id,
  outletName: z.string(),
  reason: ReviewDisputeReasonSchema,
  note: z.string().nullable(),
  checks: DisputeChecksSchema,
  /** What the auto checks suggest (Ops still decide). */
  suggestion: ReviewDecisionSchema,
  status: DisputeStatusSchema,
  decision: ReviewDecisionSchema.nullable(),
  dueAt: z.string(),
  createdAt: z.string(),
  decidedBy: z.string().nullable(),
  decisionNote: z.string().nullable(),
  version: z.number().int(),
  review: OpsReviewSchema,
});
export type ReviewDispute = z.infer<typeof ReviewDisputeSchema>;

export const DisputeQuerySchema = z.object({
  status: DisputeStatusSchema.optional(),
  limit: z.coerce.number().int().min(1).max(500).default(200),
});
export type DisputeQuery = z.infer<typeof DisputeQuerySchema>;

export const ReviewDisputeListSchema = z.object({ rows: z.array(ReviewDisputeSchema) });
export type ReviewDisputeList = z.infer<typeof ReviewDisputeListSchema>;

/* ---------------- restaurant integrity + hygiene ---------------- */

export const ScorePartSchema = z.object({
  key: z.string(),
  label: z.string(),
  value: z.string(),
  points: z.number(),
});
export type ScorePart = z.infer<typeof ScorePartSchema>;

export const RestaurantScoreSchema = z.object({
  outletId: Id,
  outletName: z.string(),
  townName: z.string().nullable(),
  status: z.string(),
  rating: z.number().nullable(),
  ratingCount: z.number().int(),
  integrity: z.number().int(),
  integrityParts: z.array(ScorePartSchema),
  topSlotsExcluded: z.boolean(),
  hygiene: z.number().int(),
  hygieneParts: z.array(ScorePartSchema),
  badge: HygieneBadgeSchema,
  reauditOpen: z.boolean(),
  computedAt: z.string(),
});
export type RestaurantScore = z.infer<typeof RestaurantScoreSchema>;

export const RestaurantScoreQuerySchema = z.object({
  filter: z.enum(['all', 'low_integrity', 'hygiene_hidden', 'reaudit']).default('all'),
  limit: z.coerce.number().int().min(1).max(1000).default(500),
});
export type RestaurantScoreQuery = z.infer<typeof RestaurantScoreQuerySchema>;

export const RestaurantScoreListSchema = z.object({ rows: z.array(RestaurantScoreSchema) });
export type RestaurantScoreList = z.infer<typeof RestaurantScoreListSchema>;

export const REAUDIT_TRIGGERS = [
  'foreign_object',
  'quality_complaints',
  'seal_spills',
  'safety_complaint',
  'manual',
] as const;
export const ReauditTriggerSchema = z.enum(REAUDIT_TRIGGERS);
export type ReauditTrigger = z.infer<typeof ReauditTriggerSchema>;

export const REAUDIT_TRIGGER_LABELS: Record<ReauditTrigger, string> = {
  foreign_object: 'Foreign object confirmed',
  quality_complaints: 'Quality complaints above limit',
  seal_spills: 'Repeated seal spills',
  safety_complaint: 'Safety complaint',
  manual: 'Opened by Ops',
};

export const ReauditCaseSchema = z.object({
  id: Id,
  outletId: Id,
  outletName: z.string(),
  trigger: ReauditTriggerSchema,
  facts: z.array(z.string()),
  status: z.enum(['open', 'closed']),
  openedAt: z.string(),
  fixBy: z.string(),
  overdue: z.boolean(),
  /** The Fleet Captain visit task text. */
  captainTask: z.string(),
  closedBy: z.string().nullable(),
  closedAt: z.string().nullable(),
  closeNote: z.string().nullable(),
  photos: z.array(ReviewPhotoSchema),
  version: z.number().int(),
});
export type ReauditCase = z.infer<typeof ReauditCaseSchema>;

export const ReauditQuerySchema = z.object({
  status: z.enum(['open', 'closed']).optional(),
  outletId: Id.optional(),
});
export type ReauditQuery = z.infer<typeof ReauditQuerySchema>;

export const ReauditListSchema = z.object({ rows: z.array(ReauditCaseSchema) });
export type ReauditList = z.infer<typeof ReauditListSchema>;

export const ReauditOpenInputSchema = z.object({
  outletId: Id,
  note: z.string().trim().min(3).max(300),
});
export type ReauditOpenInput = z.infer<typeof ReauditOpenInputSchema>;

export const ReauditCloseInputSchema = TrustDecisionInputSchema.extend({
  photoIds: z.array(Id).min(1).max(REAUDIT_MAX_PHOTOS),
});
export type ReauditCloseInput = z.infer<typeof ReauditCloseInputSchema>;

/** Restaurant drawer badge (Capsico OS) — scores of one outlet. */
export const OutletTrustSchema = z.object({
  outletId: Id,
  score: RestaurantScoreSchema.nullable(),
  openFlags: z.number().int(),
  openReaudit: ReauditCaseSchema.nullable(),
});
export type OutletTrust = z.infer<typeof OutletTrustSchema>;

/* ---------------- summary (number cards) ---------------- */

export const TrustSummarySchema = z.object({
  openFlags: z.number().int(),
  redCustomers: z.number().int(),
  heldReviews: z.number().int(),
  openDisputes: z.number().int(),
  overdueDisputes: z.number().int(),
  openReaudits: z.number().int(),
  lowIntegrity: z.number().int(),
  lastNightlyAt: z.string().nullable(),
});
export type TrustSummary = z.infer<typeof TrustSummarySchema>;

/** job:trust-nightly result. */
export const TrustNightlyResultSchema = z.object({
  customers: z.number().int(),
  riders: z.number().int(),
  restaurants: z.number().int(),
  flags: z.number().int(),
  reaudits: z.number().int(),
  licenceReminders: z.number().int(),
  paused: z.number().int(),
  photosHashed: z.number().int(),
});
export type TrustNightlyResult = z.infer<typeof TrustNightlyResultSchema>;
